import cn from 'classnames';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Tabs from '@/components/ui/Tabs';
import type { KnownSectionSlug } from '@/entities/OrganizationSection';
import { useOrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';

import DocumentsContent from '../DocumentsContent';
import MacroContent from '../MacroContent/MacroContent';
import NewsContent from '../NewsContent';
import NoteContent from '../NoteContent';

import { getSidebarTabs, TabKey } from './config';
import { getTabRoute } from './config';
import { useActiveTab } from './useActiveTab';

import s from './Sidebar.module.scss';

type SidebarProps = {
  className?: string;
};

const TABS_CONTENT_COMPONENT_MAP: Record<TabKey, React.FC> = {
  [TabKey.documents]: DocumentsContent,
  [TabKey.news]: NewsContent,
  [TabKey.notebook]: NoteContent,
  [TabKey.macro]: MacroContent,
};

const Sidebar: React.FC<SidebarProps> = ({ className }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const sectionsStore = useOrganizationSectionsStore();
  const activeTab = useActiveTab(Number(id));
  const tabs = getSidebarTabs(sectionsStore.enabledTopLevelSections).map((tab) => ({
    ...tab,
    value: sectionsStore.getSectionName(tab.key as KnownSectionSlug) ?? tab.value,
  }));

  const handleTabChange = React.useCallback(
    (key: string) => {
      navigate(getTabRoute(key as TabKey, Number(id)));
    },
    [navigate, id],
  );

  const ContentComponent = TABS_CONTENT_COMPONENT_MAP[activeTab as TabKey];

  return (
    <div className={cn(s.root, className)}>
      <div className={s.root__header}>
        <Tabs items={tabs} activeKey={activeTab} onChange={handleTabChange} />
      </div>
      <div className={s.root__content}>{ContentComponent && <ContentComponent />}</div>
    </div>
  );
};

export default Sidebar;
